// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import LazyImage from "@guestsupply/MobiusOverrides/LazyImage/LazyImage";
import Tooltip, { TooltipPresentationProps } from "@guestsupply/MobiusOverrides/Tooltip/Tooltip";
import { ellipsis } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import { IconWrapper } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import styled, { css } from "styled-components";

export enum BadgeTypeValues {
    Image = "Image",
    Text = "Text",
}

export interface BadgePresentationProps {
    textBadgeCss?: InjectableCss;
    imageBadgeProps?: JSX.IntrinsicElements["img"];
    // XNG-CHANGES
    iconStyledWrapper?: InjectableCss;
    tooltipStyles?: TooltipPresentationProps;
    imageWrapperStyles?: InjectableCss;
}
// XNG-CHANGES
export const badgePresentationProps: BadgePresentationProps = {
    iconStyledWrapper: {
        css: css`
            display: flex;
            &&& {
                ${IconWrapper} {
                    width: auto;
                    svg {
                        width: auto;
                    }
                }
            }
        `,
    },
    tooltipStyles: {
        cssOverrides: {
            tooltipClickable: css`
                margin-left: 0;
                margin-right: 0;
            `,
            tooltipWrapper: css`
                display: flex;
            `,
        },
    },
    imageWrapperStyles: {
        css: css`
            width: auto;
            height: 24px;
        `,
    },
};
const styles = badgePresentationProps;
// xng-changes : add badge custom style
const TextBadge = styled.div<{ backgroundColor: string; color: string; badgeTextRounding: number }>`
    display: flex;
    text-align: center;
    position: relative;
    padding: 4px 8px;
    background-color: #${props => props.backgroundColor};
    color: #${props => props.color};
    border-radius: ${props => props.badgeTextRounding}px;
    line-height: 16px;
    font-size: 10px;
    font-weight: bold;
    ${TypographyStyle} {
        width: 100%;
        overflow: hidden;
        ${ellipsis(1)}
    }
`;

export type BadgeProps = {
    /** When type is Image an img tag will be rendered with the src being the value prop.
     * When the type is text a textbox will be rendered with the given value used as text and color scheme
     * denoted in badgeTextColor and badgeBackgroundColor
     * When no type is given or no corresponding value is given, nothing will render.
     */
    type: BadgeTypeValues;
    /** URL image path or badge text */
    value?: string;
    /** Alt text for the image badge */
    imageAltText?: string;
    /** Hex code for badge text color
     * Default is white (FFFFFF)
     */
    badgeTextColor?: string;
    /** Hex code for badge background color
     * Default is black (000000)
     */
    badgeBackgroundColor?: string;
    /** Number used to determine border radius for text badge in pixels
     * Default is 0
     */
    badgeTextRounding?: number;
} & BadgePresentationProps;

const Badge = ({
    type,
    value,
    imageAltText,
    badgeTextColor,
    badgeBackgroundColor,
    badgeTextRounding,
    imageBadgeProps,
    textBadgeCss,
}: BadgeProps) => {
    if (!type || !value) {
        return null;
    }

    if (type === BadgeTypeValues.Image) {
        return (
            <Tooltip
                {...styles.tooltipStyles}
                text={imageAltText}
                triggerComponent={
                    // XNG-CHANGES: Check if the icon is a string or svg
                    typeof value === "string" ? (
                        <StyledWrapper {...styles.imageWrapperStyles}>
                            <LazyImage
                                width="22"
                                height="22"
                                imgProps={imageBadgeProps}
                                src={value}
                                altText={imageAltText}
                                customTransformations={{ default: "GUEST_Language_24x24" }}
                            />
                        </StyledWrapper>
                    ) : (
                        <StyledWrapper {...styles.iconStyledWrapper}>
                            <Icon src={value} />
                        </StyledWrapper>
                    )
                }
            />
        );
    }

    if (type === BadgeTypeValues.Text) {
        return (
            <TextBadge
                backgroundColor={badgeBackgroundColor || "000000"}
                color={badgeTextColor || "FFFFFF"}
                badgeTextRounding={badgeTextRounding || 4}
            >
                <Typography {...textBadgeCss}>{value}</Typography>
            </TextBadge>
        );
    }

    return <>Unknown badge type</>;
};

/** @component */
export default Badge;
